import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { onCallSignal, onGroupCallSignal, useSendCallSignal, useSendGroupCallSignal, type CallSignalEvent, type GroupCallSignalEvent } from "@/lib/realtime";
import { toast } from "sonner";

export type GroupCallStatus = "idle" | "connecting" | "active";

interface OfferPayload {
  sdp: RTCSessionDescriptionInit;
  withVideo: boolean;
  isGroupCall: true;
}
interface AnswerPayload {
  sdp: RTCSessionDescriptionInit;
}
interface IceCandidatePayload {
  candidate: RTCIceCandidateInit;
}

export interface GroupCallParticipant {
  userId: string;
  /** null until their track actually arrives - the tile still exists (so the
   * UI can show "connecting…" for them) before then. */
  stream: MediaStream | null;
}

interface GroupCallContextValue {
  status: GroupCallStatus;
  conversationId: string | null;
  withVideo: boolean;
  localStream: MediaStream | null;
  participants: GroupCallParticipant[];
  isMuted: boolean;
  isCameraOff: boolean;
  joinGroupCall: (conversationId: string, withVideo: boolean) => Promise<void>;
  leaveGroupCall: () => void;
  toggleMute: () => void;
  toggleCamera: () => void;
}

const GroupCallContext = createContext<GroupCallContextValue | null>(null);

// Same cached fetch as useCall.tsx - the two hooks each keep their own copy
// rather than sharing a module export, since they're deliberately independent
// systems that happen to both need this.
let stunServersPromise: Promise<string[]> | null = null;
function getStunServers(): Promise<string[]> {
  if (!stunServersPromise) {
    stunServersPromise = api
      .get<{ stunServers: string[] }>("/api/calls/config")
      .then((r) => r.stunServers)
      .catch(() => ["stun:stun.l.google.com:19302"]);
  }
  return stunServersPromise;
}

export function GroupCallProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const sendCallSignal = useSendCallSignal();
  const sendGroupCallSignal = useSendGroupCallSignal();

  const [status, setStatus] = useState<GroupCallStatus>("idle");
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [withVideo, setWithVideo] = useState(false);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [participants, setParticipants] = useState<GroupCallParticipant[]>([]);
  const [isMuted, setIsMuted] = useState(false);
  const [isCameraOff, setIsCameraOff] = useState(false);

  const localStreamRef = useRef<MediaStream | null>(null);
  const peerConnectionsRef = useRef<Map<string, RTCPeerConnection>>(new Map());
  const pendingCandidatesRef = useRef<Map<string, RTCIceCandidateInit[]>>(new Map());
  const remoteDescriptionSetRef = useRef<Map<string, boolean>>(new Map());

  const stateRef = useRef({ status, conversationId, withVideo });
  stateRef.current = { status, conversationId, withVideo };

  function updateParticipantStream(peerUserId: string, stream: MediaStream | null) {
    setParticipants((prev) => {
      const idx = prev.findIndex((p) => p.userId === peerUserId);
      if (idx === -1) return [...prev, { userId: peerUserId, stream }];
      const next = [...prev];
      next[idx] = { ...next[idx], stream };
      return next;
    });
  }

  function removeParticipant(peerUserId: string) {
    peerConnectionsRef.current.get(peerUserId)?.close();
    peerConnectionsRef.current.delete(peerUserId);
    pendingCandidatesRef.current.delete(peerUserId);
    remoteDescriptionSetRef.current.delete(peerUserId);
    setParticipants((prev) => prev.filter((p) => p.userId !== peerUserId));
  }

  const cleanup = useCallback(() => {
    for (const pc of peerConnectionsRef.current.values()) pc.close();
    peerConnectionsRef.current.clear();
    pendingCandidatesRef.current.clear();
    remoteDescriptionSetRef.current.clear();
    localStreamRef.current?.getTracks().forEach((t) => t.stop());
    localStreamRef.current = null;
    setLocalStream(null);
    setParticipants([]);
    setIsMuted(false);
    setIsCameraOff(false);
    setConversationId(null);
    setStatus("idle");
  }, []);

  const flushPendingCandidates = useCallback(async (peerUserId: string) => {
    const pc = peerConnectionsRef.current.get(peerUserId);
    if (!pc) return;
    const queued = pendingCandidatesRef.current.get(peerUserId) ?? [];
    for (const candidate of queued) {
      try {
        await pc.addIceCandidate(candidate);
      } catch {
        // A candidate that no longer applies is fine to drop, same reasoning
        // as the 1:1 system's identical handling.
      }
    }
    pendingCandidatesRef.current.set(peerUserId, []);
  }, []);

  const createPeerConnectionFor = useCallback(
    async (peerUserId: string, convId: string) => {
      const stunServers = await getStunServers();
      const pc = new RTCPeerConnection({ iceServers: [{ urls: stunServers }] });

      pc.onicecandidate = (event) => {
        if (event.candidate) {
          sendCallSignal({
            type: "call:ice-candidate",
            toUserId: peerUserId,
            conversationId: convId,
            payload: { candidate: event.candidate.toJSON() } satisfies IceCandidatePayload
          });
        }
      };

      pc.ontrack = (event) => {
        updateParticipantStream(peerUserId, event.streams[0] ?? null);
      };

      pc.onconnectionstatechange = () => {
        if (pc.connectionState === "failed" || pc.connectionState === "closed") {
          // Only this one participant's connection failed - a group call
          // doesn't end just because one peer connection had trouble, unlike
          // the 1:1 system where a failure ends the only connection there is.
          removeParticipant(peerUserId);
        }
      };

      localStreamRef.current?.getTracks().forEach((track) => {
        if (localStreamRef.current) pc.addTrack(track, localStreamRef.current);
      });

      peerConnectionsRef.current.set(peerUserId, pc);
      return pc;
    },
    [sendCallSignal]
  );

  // The joiner always initiates to everyone already present - both on first
  // join (via call:group-joined's participant list) and whenever someone
  // else joins after (via call:participant-joined). This one consistent rule
  // - "whoever is arriving opens the connection" - is what avoids glare
  // (both sides simultaneously offering each other), without needing a
  // separate negotiation-role protocol.
  const connectToPeer = useCallback(
    async (peerUserId: string, convId: string, video: boolean) => {
      const pc = await createPeerConnectionFor(peerUserId, convId);
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      sendCallSignal({
        type: "call:offer",
        toUserId: peerUserId,
        conversationId: convId,
        payload: { sdp: offer, withVideo: video, isGroupCall: true } satisfies OfferPayload
      });
    },
    [createPeerConnectionFor, sendCallSignal]
  );

  const joinGroupCall = useCallback(
    async (convId: string, video: boolean) => {
      if (stateRef.current.status !== "idle") return;

      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true, video });
      } catch {
        toast(video ? "Couldn't access camera/microphone" : "Couldn't access microphone");
        return;
      }
      localStreamRef.current = stream;
      setLocalStream(stream);
      setConversationId(convId);
      setWithVideo(video);
      setStatus("connecting");

      sendGroupCallSignal({ type: "call:join-group", conversationId: convId });
      // The rest (connecting to whoever's already there) happens once
      // call:group-joined comes back with the participant list - see the
      // listener below. Nothing more to do here until then.
    },
    [sendGroupCallSignal]
  );

  const leaveGroupCall = useCallback(() => {
    const convId = stateRef.current.conversationId;
    if (convId) sendGroupCallSignal({ type: "call:leave-group", conversationId: convId });
    cleanup();
  }, [sendGroupCallSignal, cleanup]);

  const toggleMute = useCallback(() => {
    const stream = localStreamRef.current;
    if (!stream) return;
    const nextMuted = !isMuted;
    stream.getAudioTracks().forEach((t) => (t.enabled = !nextMuted));
    setIsMuted(nextMuted);
  }, [isMuted]);

  const toggleCamera = useCallback(() => {
    const stream = localStreamRef.current;
    if (!stream) return;
    const nextOff = !isCameraOff;
    stream.getVideoTracks().forEach((t) => (t.enabled = !nextOff));
    setIsCameraOff(nextOff);
  }, [isCameraOff]);

  useEffect(() => {
    const unsubscribeGroup = onGroupCallSignal((event: GroupCallSignalEvent) => {
      const { conversationId: activeConvId, withVideo: activeVideo } = stateRef.current;
      if (!activeConvId || event.conversationId !== activeConvId) return;

      if (event.type === "call:group-joined") {
        setStatus("active");
        for (const existingUserId of event.participants) {
          updateParticipantStream(existingUserId, null);
          void connectToPeer(existingUserId, activeConvId, activeVideo);
        }
        return;
      }

      if (event.type === "call:participant-joined") {
        updateParticipantStream(event.userId, null);
        return;
      }

      if (event.type === "call:participant-left") {
        removeParticipant(event.userId);
        return;
      }
    });

    const unsubscribeCall = onCallSignal((event: CallSignalEvent) => {
      const { conversationId: activeConvId } = stateRef.current;
      if (!activeConvId || event.conversationId !== activeConvId) return;

      if (event.type === "call:offer") {
        const payload = event.payload as OfferPayload;
        if (!payload.isGroupCall) return;
        void (async () => {
          await createPeerConnectionFor(event.fromUserId, activeConvId);
          const pc = peerConnectionsRef.current.get(event.fromUserId);
          if (!pc) return;
          await pc.setRemoteDescription(payload.sdp);
          remoteDescriptionSetRef.current.set(event.fromUserId, true);
          await flushPendingCandidates(event.fromUserId);
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          sendCallSignal({
            type: "call:answer",
            toUserId: event.fromUserId,
            conversationId: activeConvId,
            payload: { sdp: answer } satisfies AnswerPayload
          });
        })();
        return;
      }

      if (event.type === "call:answer") {
        const pc = peerConnectionsRef.current.get(event.fromUserId);
        if (!pc) return;
        const payload = event.payload as AnswerPayload;
        pc.setRemoteDescription(payload.sdp)
          .then(() => {
            remoteDescriptionSetRef.current.set(event.fromUserId, true);
            return flushPendingCandidates(event.fromUserId);
          })
          .catch(() => removeParticipant(event.fromUserId));
        return;
      }

      if (event.type === "call:ice-candidate") {
        const payload = event.payload as IceCandidatePayload;
        if (remoteDescriptionSetRef.current.get(event.fromUserId) && peerConnectionsRef.current.has(event.fromUserId)) {
          peerConnectionsRef.current.get(event.fromUserId)!.addIceCandidate(payload.candidate).catch(() => {});
        } else {
          const queue = pendingCandidatesRef.current.get(event.fromUserId) ?? [];
          queue.push(payload.candidate);
          pendingCandidatesRef.current.set(event.fromUserId, queue);
        }
        return;
      }
    });

    return () => {
      unsubscribeGroup();
      unsubscribeCall();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      for (const pc of peerConnectionsRef.current.values()) pc.close();
      localStreamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  useEffect(() => {
    if (!user && stateRef.current.status !== "idle") leaveGroupCall();
  }, [user, leaveGroupCall]);

  const value: GroupCallContextValue = {
    status,
    conversationId,
    withVideo,
    localStream,
    participants,
    isMuted,
    isCameraOff,
    joinGroupCall,
    leaveGroupCall,
    toggleMute,
    toggleCamera
  };

  return <GroupCallContext.Provider value={value}>{children}</GroupCallContext.Provider>;
}

export function useGroupCall() {
  const ctx = useContext(GroupCallContext);
  if (!ctx) throw new Error("useGroupCall must be used within GroupCallProvider");
  return ctx;
}
